import React, { useEffect, useMemo, useState } from "react";
import { useLang } from "@/context/LangContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { API_BASE, getErrorDetail } from "@/lib/api";
import { format, getISOWeek, getISOWeekYear, addDays } from "date-fns";
import { Check, Download } from "lucide-react";
import ConsultantPanel from "@/components/ConsultantPanel";

const PROGRAMS = ["Primary","K-12","Teens","IB","Cambridge KET/PET/FCE","A-Level"];

function isoWeekKey(date) {
  return `${getISOWeekYear(date)}-W${String(getISOWeek(date)).padStart(2, "0")}`;
}

function dateKey(d) { return format(d, "yyyy-MM-dd"); }

function programToAgeBand(program) {
  switch (program) {
    case "Primary": return "primary";
    case "Teens": return "teens";
    case "K-12": return "k12";
    case "IB":
    case "Cambridge KET/PET/FCE":
    case "A-Level":
      return "ib_alevel_cpe";
    default: return "primary";
  }
}

function BookingForm({ bookingType, requestedTeacher }) {
  const { t, lang } = useLang();
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");
  const [program, setProgram] = useState("");
  const [form, setForm] = useState({ name:"", email:"", phone:"", notes:"" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [slotsByDay, setSlotsByDay] = useState({});
  const [cal, setCal] = useState({ holidays: [], events: [], full_weeks: [], week_cap: 5 });

  // Apply the requested teacher to the trial-class notes when it changes.
  useEffect(() => {
    // Apply the requested teacher to the trial-class notes when it changes.
    if (!requestedTeacher || bookingType !== "trial_class") return;
    const tag = lang === "zh"
      ? `偏好老师：${requestedTeacher}\n`
      : `Preferred teacher: ${requestedTeacher}\n`;
    setForm((f) => f.notes.includes(`: ${requestedTeacher}`) ? f : { ...f, notes: tag + (f.notes || "") });
  }, [requestedTeacher, bookingType, lang]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/booking-slots");
        const map = {};
        (data?.days || []).forEach((d) => { map[d.day_of_week] = d.slots || []; });
        setSlotsByDay(map);
      } catch (e) { /* keep empty */ }
    })();
    (async () => {
      try {
        const today = new Date();
        const start = format(today, "yyyy-MM-dd");
        const end = format(addDays(today, 180), "yyyy-MM-dd");
        const { data } = await api.get(`/calendar?start=${start}&end=${end}`);
        setCal({
          holidays: data?.holidays || [],
          events: data?.events || [],
          full_weeks: data?.full_weeks || [],
          week_cap: data?.week_cap || 5,
        });
      } catch (e) { /* keep empty */ }
    })();
  }, []);

  // Helpers — expand multi-day events into all dates they cover so the booking
  // calendar visually matches the admin grid (same source of truth: /api/calendar).
  function expandRange(startISO, endISO) {
    const out = [];
    const start = new Date(startISO + "T00:00:00");
    const end = new Date((endISO || startISO) + "T00:00:00");
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      out.push(d.toISOString().slice(0, 10));
    }
    return out;
  }

  // Build lookup sets for the calendar modifiers
  const holidayMap = useMemo(() => {
    const m = new Map();
    cal.holidays.forEach((h) => { m.set(h.date, h); });
    return m;
  }, [cal.holidays]);
  const offDayMap = useMemo(() => {
    const m = new Map();
    cal.events.filter(e => e.type === "off_day").forEach((e) => {
      expandRange(e.date, e.end_date).forEach((k) => m.set(k, e));
    });
    return m;
  }, [cal.events]);
  const activityMap = useMemo(() => {
    const m = new Map();
    cal.events.filter(e => e.type === "activity").forEach((e) => {
      expandRange(e.date, e.end_date).forEach((k) => m.set(k, e));
    });
    return m;
  }, [cal.events]);
  const fullWeekSet = useMemo(() => new Set(cal.full_weeks || []), [cal.full_weeks]);

  const isHoliday = (d) => holidayMap.has(dateKey(d));
  const isOffDay = (d) => offDayMap.has(dateKey(d));
  const isActivity = (d) => activityMap.has(dateKey(d));
  const isFullForInterview = (d) => bookingType === "interview" && fullWeekSet.has(isoWeekKey(d));

  const dayKey = date ? format(date, "EEE") : null;  // Mon, Tue, ...
  const availableSlots = useMemo(() => (dayKey ? (slotsByDay[dayKey] || []) : []), [dayKey, slotsByDay]);
  const allDaysClosed = Object.values(slotsByDay).every((s) => !s || s.length === 0);

  useEffect(() => {
    if (time && !availableSlots.includes(time)) setTime("");
  }, [availableSlots]); // eslint-disable-line react-hooks/exhaustive-deps

  // Marker info under the selected date
  const selectedMarker = useMemo(() => {
    if (!date) return null;
    const k = dateKey(date);
    if (holidayMap.has(k)) return { tone: "holiday", item: holidayMap.get(k) };
    if (offDayMap.has(k)) return { tone: "off_day", item: offDayMap.get(k) };
    if (activityMap.has(k)) return { tone: "activity", item: activityMap.get(k) };
    if (isFullForInterview(date)) return { tone: "full", item: null };
    return null;
  }, [date, holidayMap, offDayMap, activityMap, fullWeekSet]); // eslint-disable-line

  async function submit(e) {
    e.preventDefault();
    if (!date) { toast.error(t.booking.pick_date); return; }
    if (!time) { toast.error(t.booking.time); return; }
    if (!program) { toast.error(t.booking.program); return; }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        booking_type: bookingType,
        name: form.name, email: form.email, phone: form.phone,
        program, preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: time, notes: form.notes,
      });
      toast.success(t.booking.success);
      setConfirmation({
        email: form.email.trim().toLowerCase(),
        program,
        ageBand: programToAgeBand(program),
      });
    } catch (e) {
      toast.error(getErrorDetail(e));
    } finally { setSubmitting(false); }
  }

  function resetForm() {
    setForm({ name:"", email:"", phone:"", notes:"" });
    setTime(""); setProgram(""); setDate(undefined);
    setConfirmation(null);
  }

  if (confirmation) {
    return (
      <div data-testid={`booking-thanks-${bookingType}`} className="mt-6 rounded-2xl border border-[hsl(var(--border))] bg-white p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-7">
          <div className="w-12 h-12 rounded-full bg-[hsl(var(--secondary))]/10 flex items-center justify-center">
            <Check className="w-6 h-6 text-[hsl(var(--secondary))]" />
          </div>
          <p className="mt-5 text-xs tracking-[0.25em] text-[hsl(var(--muted-foreground))]">{t.booking.thanks_eyebrow}</p>
          <h3 className="font-display text-3xl md:text-4xl tracking-tight font-medium mt-2">{t.booking.thanks_title}</h3>
          <p className="mt-3 text-[hsl(var(--muted-foreground))] max-w-md">{t.booking.thanks_body}</p>
          <Button variant="outline" onClick={resetForm} data-testid={`booking-another-${bookingType}`} className="rounded-full mt-7">
            {t.booking.book_another}
          </Button>
        </div>
        <div className="md:col-span-5 rounded-2xl bg-[hsl(var(--muted))] p-6 border border-[hsl(var(--border))]">
          <p className="text-xs tracking-[0.25em] uppercase text-[hsl(var(--primary))]">{t.booking.gift_eyebrow}</p>
          <h4 className="font-display text-xl tracking-tight font-medium mt-2">{t.booking.gift_title}</h4>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">{t.booking.gift_body}</p>
          <Button
            asChild
            data-testid={`booking-download-${bookingType}`}
            className="w-full mt-5 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-full lift"
          >
            <a
              href={`${API_BASE}/planner/${confirmation.ageBand}?email=${encodeURIComponent(confirmation.email)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Download className="w-4 h-4 mr-2" />
              {t.booking.gift_btn}
            </a>
          </Button>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-3">{t.booking.gift_hint.replace("{program}", confirmation.program)}</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-12 gap-8 mt-6" data-testid={`booking-form-${bookingType}`}>
      <div className="md:col-span-5 bg-[hsl(var(--muted))] rounded-2xl p-4 border border-[hsl(var(--border))]">
        <Label className="text-xs tracking-[0.2em] uppercase text-[hsl(var(--muted-foreground))]">{t.booking.date}</Label>
        <Calendar
          mode="single"
          selected={date}
          onSelect={setDate}
          disabled={(d) => {
            if (d < new Date(new Date().setHours(0,0,0,0))) return true;
            if (isHoliday(d)) return true;
            if (isOffDay(d)) return true;
            if (isFullForInterview(d)) return true;
            return false;
          }}
          modifiers={{
            holiday: (d) => isHoliday(d),
            offDay: (d) => isOffDay(d),
            activity: (d) => isActivity(d),
            fullWeek: (d) => isFullForInterview(d),
          }}
          modifiersClassNames={{
            holiday: "dv-day-holiday",
            offDay: "dv-day-off",
            activity: "dv-day-activity",
            fullWeek: "dv-day-full",
          }}
          className="rounded-md mt-2 bg-white"
          data-testid={`booking-calendar-${bookingType}`}
        />
        {selectedMarker && (
          <div data-testid={`booking-date-note-${bookingType}`} className={`mt-3 text-xs px-3 py-2 rounded-lg border ${
            selectedMarker.tone === "holiday" ? "bg-[hsl(var(--destructive))]/5 border-[hsl(var(--destructive))]/30 text-[hsl(var(--destructive))]" :
            selectedMarker.tone === "off_day" ? "bg-[hsl(var(--foreground))]/5 border-[hsl(var(--border))] text-[hsl(var(--foreground))]" :
            selectedMarker.tone === "activity" ? "bg-[hsl(var(--primary))]/5 border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))]" :
            "bg-[hsl(var(--muted))] border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"
          }`}>
            <strong>{selectedMarker.tone === "holiday" ? t.booking.cal.holiday : selectedMarker.tone === "off_day" ? t.booking.cal.off_day : selectedMarker.tone === "activity" ? t.booking.cal.activity : t.booking.cal.full_week}</strong>
            {selectedMarker.item && <> · {lang === "zh" && selectedMarker.item.title_zh ? selectedMarker.item.title_zh : selectedMarker.item.title}</>}
          </div>
        )}
        <ul data-testid={`booking-calendar-legend-${bookingType}`} className="mt-4 space-y-1.5 text-[11px] text-[hsl(var(--muted-foreground))]">
          <li className="flex items-center gap-2"><span className="dv-legend-dot dv-legend-available" />{t.booking.cal.legend_available}</li>
          <li className="flex items-center gap-2"><span className="dv-legend-dot dv-legend-holiday" />{t.booking.cal.legend_holiday}</li>
          <li className="flex items-center gap-2"><span className="dv-legend-dot dv-legend-off" />{t.booking.cal.legend_off}</li>
          <li className="flex items-center gap-2"><span className="dv-legend-dot dv-legend-activity" />{t.booking.cal.legend_activity}</li>
          {bookingType === "interview" && (
            <li className="flex items-center gap-2"><span className="dv-legend-dot dv-legend-full" />{t.booking.cal.legend_full.replace("{cap}", String(cal.week_cap))}</li>
          )}
        </ul>
      </div>
      <div className="md:col-span-7 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor={`name-${bookingType}`} className="text-xs tracking-[0.2em] uppercase text-[hsl(var(--muted-foreground))]">{t.booking.name}</Label>
            <Input id={`name-${bookingType}`} required data-testid={`booking-name-${bookingType}`} value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="mt-2" />
          </div>
          <div>
            <Label htmlFor={`email-${bookingType}`} className="text-xs tracking-[0.2em] uppercase text-[hsl(var(--muted-foreground))]">{t.booking.email}</Label>
            <Input id={`email-${bookingType}`} required type="email" data-testid={`booking-email-${bookingType}`} value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="mt-2" />
          </div>
          <div>
            <Label htmlFor={`phone-${bookingType}`} className="text-xs tracking-[0.2em] uppercase text-[hsl(var(--muted-foreground))]">{t.booking.phone}</Label>
            <Input id={`phone-${bookingType}`} required data-testid={`booking-phone-${bookingType}`} value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} className="mt-2" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.2em] uppercase text-[hsl(var(--muted-foreground))]">{t.booking.program}</Label>
            <Select value={program} onValueChange={setProgram}>
              <SelectTrigger data-testid={`booking-program-${bookingType}`} className="mt-2"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {PROGRAMS.map((p)=>(<SelectItem key={p} value={p} data-testid={`booking-program-opt-${p}`}>{p}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs tracking-[0.2em] uppercase text-[hsl(var(--muted-foreground))]">{t.booking.time}</Label>
          {!date ? (
            <p data-testid={`booking-time-empty-${bookingType}`} className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">{t.booking.pick_date_first}</p>
          ) : availableSlots.length === 0 ? (
            <p data-testid={`booking-time-closed-${bookingType}`} className="mt-2 text-sm text-[hsl(var(--destructive))]">
              {allDaysClosed ? t.booking.no_slots_global : t.booking.no_slots_day.replace("{day}", dayKey)}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2 mt-2" data-testid={`booking-time-grid-${bookingType}`}>
              {availableSlots.map((s)=>(
                <button
                  key={s}
                  type="button"
                  onClick={()=>setTime(s)}
                  data-testid={`booking-time-${bookingType}-${s}`}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${time===s?'bg-[hsl(var(--foreground))] text-[hsl(var(--background))] border-[hsl(var(--foreground))]':'bg-white border-[hsl(var(--border))] hover:border-[hsl(var(--foreground))]'}`}
                >{s}</button>
              ))}
            </div>
          )}
        </div>
        <div>
          <Label htmlFor={`notes-${bookingType}`} className="text-xs tracking-[0.2em] uppercase text-[hsl(var(--muted-foreground))]">{t.booking.notes}</Label>
          <Textarea id={`notes-${bookingType}`} data-testid={`booking-notes-${bookingType}`} value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} className="mt-2" rows={3} />
        </div>
        <Button type="submit" disabled={submitting} data-testid={`booking-submit-${bookingType}`} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-full px-6 lift">
          {submitting ? "…" : t.booking.submit}
        </Button>
      </div>
    </form>
  );
}

export default function BookingSection() {
  const { t } = useLang();
  const [activeTab, setActiveTab] = useState("trial_class");
  const [requestedTeacher, setRequestedTeacher] = useState(null);

  useEffect(() => {
    function applyFromStorage() {
      try {
        const name = sessionStorage.getItem("requested_teacher");
        if (name) {
          setRequestedTeacher(name);
          setActiveTab("trial_class");
          sessionStorage.removeItem("requested_teacher");
        }
      } catch (_e) { /* ignore */ }
    }
    applyFromStorage();
    function onEvt() { applyFromStorage(); }
    window.addEventListener("requested-teacher-change", onEvt);
    window.addEventListener("focus", onEvt);
    return () => {
      window.removeEventListener("requested-teacher-change", onEvt);
      window.removeEventListener("focus", onEvt);
    };
  }, []);

  return (
    <section id="booking" data-testid="booking-section" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <p className="text-xs tracking-[0.25em] text-[hsl(var(--muted-foreground))]">{t.booking.eyebrow}</p>
      <h2 className="font-display text-3xl md:text-5xl tracking-tight font-medium mt-3 max-w-3xl">{t.booking.title}</h2>
      <p className="mt-4 max-w-2xl text-[hsl(var(--muted-foreground))]">{t.booking.sub}</p>
      {requestedTeacher && (
        <div data-testid="booking-requested-teacher-banner" className="mt-6 rounded-2xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 px-5 py-3 text-sm text-[hsl(var(--foreground))] inline-flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">★</span>
          <span>You requested a trial with <strong>{requestedTeacher}</strong>. We&apos;ve noted it in your booking.</span>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-10">
        <TabsList data-testid="booking-tabs" className="bg-[hsl(var(--muted))] p-1 rounded-full h-auto">
          <TabsTrigger value="interview" data-testid="booking-tab-interview" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-5 py-2">{t.booking.tab_interview}</TabsTrigger>
          <TabsTrigger value="trial_class" data-testid="booking-tab-trial" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-5 py-2">{t.booking.tab_trial}</TabsTrigger>
          <TabsTrigger value="consultant" data-testid="booking-tab-consultant" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-5 py-2">{t.booking.tab_consultant}</TabsTrigger>
        </TabsList>
        <TabsContent value="interview"><BookingForm bookingType="interview" /></TabsContent>
        <TabsContent value="trial_class"><BookingForm bookingType="trial_class" requestedTeacher={requestedTeacher} /></TabsContent>
        <TabsContent value="consultant"><ConsultantPanel /></TabsContent>
      </Tabs>
    </section>
  );
}
